import { useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../hooks/reduxToolkit'
import { updateProvider } from '../../reduxToolkit/providersSlice'
import { present } from '../../../lib/util/present'
import { useSubscriptionPrompt } from '../../hooks/useSubscriptionPrompt'

export function ProviderAuthorizationCard({
  providerId,
  onCollapsedStateChange
}: {
  providerId: string
  onCollapsedStateChange?: (isCollapsed: boolean) => void
}) {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false)

  const { checkSubscription, SubscriptionPromptModal } = useSubscriptionPrompt({
    trigger: 'always',
    title: 'Subscribe for Provider Communication',
    message: 'Alice (our AI agent) can automatically call providers to get your itemized bills.',
  })

  const provider = useAppSelector((s) => s.providers?.providers?.find((p) => p.id === providerId))
  const active = present(provider?.communication_authorized_at)
  const grantedAt = provider?.communication_authorized_at
  const autoRequest = provider?.auto_request_itemized_bills !== false

  const dispatch = useAppDispatch()

  const grant = async () => {
    // Check subscription before allowing provider authorization
    if (!checkSubscription()) {
      return
    }

    const now = new Date().toISOString()
    dispatch(updateProvider({
      id: providerId,
      updated_at: now,
      communication_authorized_at: now
    }))
  }

  const revoke = async () => {
    const now = new Date().toISOString()
    dispatch(updateProvider({
      id: providerId,
      updated_at: now,
      communication_authorized_at: null
    }))
  }

  const toggleAutoRequest = () => {
    const now = new Date().toISOString()
    dispatch(updateProvider({
      id: providerId,
      updated_at: now,
      auto_request_itemized_bills: !autoRequest
    }))
  }

  const toggleCollapsed = () => {
    const newCollapsedState = !isCollapsed
    setIsCollapsed(newCollapsedState)
    onCollapsedStateChange?.(newCollapsedState)
  }

  if (active && isCollapsed) {
    return (
      <div
        className="card mb-4 authorization-collapsed border-success"
        onClick={toggleCollapsed}
        style={{ cursor: 'pointer' }}
      >
        <div className="card-body py-2">
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <i className="material-icons text-success me-2">check_circle</i>
              <span className="text-success fw-bold">Communication Authorized</span>
              {grantedAt && (
                <span className="badge bg-success ms-2">
                  {new Date(grantedAt).toLocaleDateString()}
                </span>
              )}
            </div>
            <i className="material-icons text-muted">expand_more</i>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <SubscriptionPromptModal />
      <div className="card mb-4 authorization-expanded">
        <div className="card-body">
        {active && (
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="card-title mb-0">Communication Authorization</h5>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={toggleCollapsed}
              type="button"
            >
              <i className="material-icons">expand_less</i>
            </button>
          </div>
        )}
        {!active && (
          <h5 className="card-title">Authorize communication with this provider</h5>
        )}
        <p>
          By enabling this, you authorize us to contact this provider on your behalf solely to request
          itemized bills associated with your recorded expenses. We will include only the minimum
          necessary information to identify your expense (e.g., your name and dates of service).
          You can revoke this at any time. We will store copies of these communications in your account.
        </p>
        <div>
          <button className={`btn ${active ? 'btn-outline-danger' : 'btn-primary'}`} onClick={active ? revoke : grant}>
            {active ? 'Revoke authorization' : 'Authorize'}
          </button>
          {active && grantedAt && (
            <span className="badge bg-success ms-2">{new Date(grantedAt).toLocaleDateString()}</span>
          )}
        </div>
        {active && (
          <div className="form-check form-switch mt-3">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              id={`auto-request-${providerId}`}
              checked={autoRequest}
              onChange={toggleAutoRequest}
            />
            <label className="form-check-label" htmlFor={`auto-request-${providerId}`}>
              Automatically request itemized bills for new expenses
            </label>
          </div>
        )}
        </div>
      </div>
    </>
  )
}
